import type { RTCSession } from 'jssip/lib/RTCSession';

import { trackSessionAcceptedAnalytics } from 'features/sip/helpers';

/**
 * Service to track RTC sessions during teaming operations.
 *
 * During teaming, we maintain references to both the direct (original) session
 * and the teaming session to handle proper event routing and cleanup.
 * This is essential for correct deaf disconnect handling and session management.
 */

type SessionRole = 'direct' | 'teaming';

class RtcSessionTrackerService {
  private directSession: RTCSession | null = null;
  private teamingSession: RTCSession | null = null;
  private dispatch: any = null;

  // ========================================
  // PUBLIC API
  // ========================================

  /**
   * Set the dispatch function for analytics
   */
  setDispatch(dispatch: any): void {
    this.dispatch = dispatch;
  }

  /**
   * Automatically track a session based on its remote URI.
   * Sessions with 'SVRS-RT' in their URI are teaming sessions,
   * all others are direct sessions.
   *
   * @param session - The RTCSession to track
   * @param dispatch - Optional dispatch function. If provided, will be set and used for analytics
   */
  trackSession(session: RTCSession, dispatch?: any): void {
    // Set dispatch if provided
    if (dispatch) {
      this.setDispatch(dispatch);
    }

    const uri = this.getSessionUri(session);
    let sessionRole: SessionRole;
    if (this.isTeamingUri(uri)) {
      this.setTeamingSession(session);
      sessionRole = 'teaming';
    } else {
      this.setDirectSession(session);
      sessionRole = 'direct';
    }

    // Call analytics after tracking
    if (this.dispatch) {
      trackSessionAcceptedAnalytics(this.dispatch, sessionRole, session);
    }
  }

  /**
   * Get the role of a given session.
   */
  getSessionRole(session: RTCSession): SessionRole | undefined {
    if (this.isDirectSession(session)) {
      return 'direct';
    }
    if (this.isTeamingSession(session)) {
      return 'teaming';
    }
    return undefined;
  }

  /**
   * Check if a session is the tracked direct session.
   */
  isDirectSession(session: RTCSession | null | undefined): boolean {
    return this.isSameSession(session, this.directSession);
  }

  /**
   * Check if a session is the tracked teaming session.
   */
  isTeamingSession(session: RTCSession | null | undefined): boolean {
    return this.isSameSession(session, this.teamingSession);
  }

  /**
   * Get the current direct session.
   */
  getDirectSession(): RTCSession | null {
    return this.directSession;
  }

  /**
   * Get the current teaming session.
   */
  getTeamingSession(): RTCSession | null {
    return this.teamingSession;
  }

  /**
   * Manually set the direct session.
   */
  setDirectSession(session: RTCSession | null): void {
    this.directSession = session;
  }

  /**
   * Manually set the teaming session.
   */
  setTeamingSession(session: RTCSession | null): void {
    this.teamingSession = session;
  }

  /**
   * Clear all tracked sessions.
   */
  clear(): void {
    this.directSession = null;
    this.teamingSession = null;
  }

  clearDirectSession(): void {
    this.directSession = null;
  }

  /**
   * Check if any sessions are currently being tracked.
   */
  hasActiveSessions(): boolean {
    return this.directSession !== null || this.teamingSession !== null;
  }

  /**
   * Determine if a URI belongs to a teaming session.
   */
  isTeamingUri(uri: string): boolean {
    return uri.includes('SVRS-RT');
  }

  // ========================================
  // PRIVATE HELPERS
  // ========================================

  /**
   * Extract URI from a session safely.
   */
  private getSessionUri(session: RTCSession | null): string {
    return session?.remote_identity?.uri?.toString() || '';
  }

  /**
   * Check if two sessions are the same instance.
   */
  private isSameSession(
    session1: RTCSession | null | undefined,
    session2: RTCSession | null
  ): boolean {
    if (!session1 || !session2) {
      return false;
    }
    return session1 === session2;
  }
}

// Export a singleton instance
export const RtcSessionTracker = new RtcSessionTrackerService();
